import React, { useState, useEffect } from 'react';
import { Check, Zap, Shield, Crown, ArrowRight } from 'lucide-react';
import { apiService } from '../api/apiclient';
import { useLocation, useNavigate } from 'react-router-dom';

const PricingCard = ({ title, price, description, features, icon: Icon, highlighted, buttonText, onButtonClick, isCurrent }) => (
    <div className={`relative flex flex-col p-8 bg-white rounded-3xl transition-all duration-300 ${highlighted
        ? 'ring-4 ring-red-500 shadow-2xl scale-105 z-10'
        : 'border border-gray-100 shadow-xl hover:shadow-2xl'
        }`}>
        {highlighted && (
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-500 text-white px-4 py-1 rounded-full text-xs font-bold tracking-widest uppercase">
                Most Popular
            </span>
        )}

        <div className="mb-8">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${highlighted ? 'bg-red-500 text-white' : 'bg-red-50 text-red-500'}`}>
                <Icon size={24} />
            </div>
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <p className="text-gray-500 text-sm mt-2">{description}</p>
        </div>

        <div className="mb-8">
            <span className="text-4xl font-black text-gray-900">${price}</span>
            <span className="text-gray-400">/mo</span>
        </div>

        <ul className="space-y-4 mb-10 flex-1">
            {features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-gray-600">
                    <Check size={18} className="text-green-500 shrink-0" />
                    {feature}
                </li>
            ))}
        </ul>

        <button
            onClick={() => { onButtonClick(title) }}
            className='w-full py-4 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-200'
        >
            {buttonText} <ArrowRight size={18} />
        </button>

    </div>
);

const SubscriptionPage = () => {
    const [userPlan, setUserPlan] = useState("free");
    const [subscription, setSubscription] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const handlePaymentCallback = async () => {
            const params = new URLSearchParams(location.search);
            const status = params.get("status");

            if (status === "success") {
                try {
                    const data = await apiService.getSubscription();
                    if (data.subscription) {
                        const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
                        if (userInfo.plan !== data.subscription.plan_type) {
                            userInfo.plan = data.subscription.plan_type;
                            userInfo.subscription = data.subscription;
                            setUserPlan(userInfo.plan);
                            setSubscription(userInfo.subscription);
                            localStorage.setItem("user", JSON.stringify(userInfo));
                            alert(`Payment successful! Your plan is now ${data.plan}`);
                        } else {
                            alert("If your payment is successful, please wait a moment and then click the Refresh button to update your subscription.");
                        }
                    }
                } catch (err) {
                    console.error("Failed to fetch subscription:", err);
                    alert("Payment successful, but failed to refresh your subscription. Please refresh later.");
                } finally {
                    navigate(location.pathname, { replace: true });
                }
            }
        };

        handlePaymentCallback();
    }, [location]);

    const handlePlanChange = async (currentPlan, userPlan) => {
        setIsLoading(true);
        try {
            if (userPlan.toLowerCase() === "free") {
                const response = await apiService.createSubscription(currentPlan);
                setIsLoading(false);
                if (response.url) {
                    window.location.href = response.url;
                } else {
                    alert("Failed to get payment link. Please try again.");
                }
            } else if (currentPlan != userPlan) {
                const response = await apiService.changeSubscription(currentPlan);
                setIsLoading(false);
                console.log(response);
                if (response.success) {
                    alert(`${response.message}`);
                    await refreshSubscription();
                } else {
                    alert("Failed to change subscription. Please try again.");
                }
            } else if (currentPlan == userPlan) {
                const response = await apiService.cancelSubscription(currentPlan);
                console.log(response);
                if (response.success) {
                    alert(`${response.message} at ${response.endsAt}`);
                } else {
                    alert("Subscription cancellation failed. Please retry later.");
                }
                setIsLoading(false);
                console.log(response);
            }
        } catch (err) {
            console.error("Subscription operation failed:", err);
            alert("Operation failed: " + err.message);
        }
        finally {
            setIsLoading(false);
        }
    };

    const plans = [
        {
            title: "Standard",
            price: "5.9",
            description: "Best for growing creators",
            icon: Zap,
            features: ["Up to 5 Accounts", "100 Posts", "500MB history storage"],
            buttonText: getButtonText(userPlan, "standard"),
            onButtonClick: (currentPlan) => {
                handlePlanChange(currentPlan.toLowerCase(), userPlan);
            },
            highlighted: true,
            isCurrent: userPlan.toLowerCase() === "standard"
        },
        {
            title: "Premium",
            price: "9.9",
            description: "For agencies and power users",
            icon: Crown,
            features: ["15 Accounts", "500 Posts", "2GB history storage"],
            buttonText: getButtonText(userPlan, "premium"),
            onButtonClick: (currentPlan) => {
                handlePlanChange(currentPlan.toLowerCase(), userPlan);
            },
            highlighted: false,
            isCurrent: userPlan.toLowerCase() === "premium"
        },
        {
            title: "Professional",
            price: "29.9",
            description: "Perfect for testing the waters",
            icon: Shield,
            features: [
                "Up to 30 Accounts",
                "1000 Posts per month",
                "3GB history storage",
            ],
            buttonText: getButtonText(userPlan, "professional"),
            onButtonClick: (currentPlan) => {
                handlePlanChange(currentPlan.toLowerCase(), userPlan);
            },
            highlighted: false,
            isCurrent: userPlan.toLowerCase() === "professional"
        },
    ];

    useEffect(() => {
        const user = localStorage.getItem('user');
        if (user) {
            const userObj = JSON.parse(user);
            setUserPlan(userObj.plan);
            setSubscription(userObj.subscription);
        }
    }, []);

    const refreshSubscription = async () => {
        try {
            setIsLoading(true);
            const data = await apiService.getSubscription();
            const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
            setIsLoading(false);
            if (userInfo.plan !== data.subscription.plan_type) {
                userInfo.plan = data.subscription.plan_type;
                userInfo.subscription = data.subscription;
                setUserPlan(userInfo.plan);
                setSubscription(userInfo.subscription);
                localStorage.setItem("user", JSON.stringify(userInfo));
                alert(`Payment successful! Your plan is now ${data.plan}`);
            } else {
                alert("No changes detected. Your plan is up to date.");
            }
        } catch (err) {
            console.error("Failed to refresh subscription:", err);
            setIsLoading(false);
            alert("Failed to refresh subscription. Please try again later.");
        } finally { setIsLoading(false); }
    };


    return (
        <div className="min-h-screen bg-gray-50/50 py-20 px-6">
            {isLoading && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center">
                        <div className="h-8 w-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                        <span className="text-gray-700 font-semibold">Processing...</span>
                    </div>
                </div>
            )}
            <div className="max-w-6xl mx-auto text-center mb-16">
                <div className="flex justify-center items-center gap-4 mb-8">
                    <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                        Simple, Transparent Pricing
                    </h1>
                    <button
                        onClick={refreshSubscription}
                        disabled={isLoading}
                        className="px-10 py-4 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-200"
                    >
                        Refresh Subscription
                    </button>
                </div>

                <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                    Choose the plan that's right for your social media growth.
                </p>
                <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                    Your current plan is <strong>{userPlan}</strong>. If this doesn't look right, click the refresh button to sync.
                </p>
                {userPlan === "free" && (
                    <p className="text-m text-gray-500 max-w-2xl mx-auto">
                        You can add multiple accounts and send bulk posts with free plan, but it is not guaranteed.
                    </p>
                )}
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                {plans.map((plan, idx) => (
                    <PricingCard key={idx} {...plan} />
                ))}
            </div>

            <div className="mt-20 text-center">
                <p className="text-gray-400 text-sm">
                    All plans include secure SSL encryption and data protection.
                    <br />
                    Need a custom enterprise solution? <a href="mailto:support@antaressocial.com" className="text-red-600 font-bold hover:underline">Chat with us</a>
                </p>
            </div>
        </div>
    );
};

const getButtonText = (userPlan, planTitle) => {
    const user = userPlan.toLowerCase();
    const plan = planTitle.toLowerCase();

    if (user === plan) return "Cancel Subscription";
    if (user === "free") return "Subscribe";

    return "Change Plan";
};


export default SubscriptionPage;